import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Box, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ReactMarkdown from "react-markdown";
import IncognitoFileChip from "./IncognitoFileChip";
import { TEXT_TYPES, IMAGE_TYPES } from "../hooks/useIncognitoChat";
import { MaskGlyph } from "./QuickChatGlyphs";
import QC from "./quickChatTheme";

const ACCEPTED_TYPES = { ...TEXT_TYPES, ...IMAGE_TYPES };

const StatusStrip = () => (
  <Box
    data-testid="quickchat-status-strip"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      padding: "10px 22px",
      backgroundColor: "rgba(0,0,0,0.35)",
      borderBottom: `1px solid ${QC.border}`,
    }}
  >
    <MaskGlyph size={18} color={QC.fgMid} />
    <Typography
      component="span"
      sx={{
        fontFamily: "Raleway, sans-serif",
        fontSize: "12px",
        fontWeight: 600,
        color: QC.fgMid,
        letterSpacing: "0.02em",
      }}
    >
      Quick chat
    </Typography>
  </Box>
);

const FileLimitCard = ({ types, size, detail }) => (
  <Box
    sx={{
      width: 220,
      padding: "14px 16px",
      borderRadius: "10px",
      border: `1px dashed ${QC.borderDashSoft}`,
      backgroundColor: "rgba(26,22,18,0.55)",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "monospace",
        fontSize: "11px",
        color: QC.fgMute,
        letterSpacing: "0.6px",
      }}
    >
      {types}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Raleway, sans-serif",
        fontSize: "14px",
        fontWeight: 600,
        color: QC.fg,
      }}
    >
      {size}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "11.5px",
        color: QC.fgMute,
      }}
    >
      {detail}
    </Typography>
  </Box>
);

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      padding: "24px",
      width: "100%",
    }}
  >
    <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
      <FileLimitCard
        types="PDF · DOCX · TXT"
        size="Up to 25 MB"
        detail="~200 pages of text"
      />
      <FileLimitCard
        types="PNG · JPG · WEBP · GIF"
        size="Up to 10 MB"
        detail="one image per turn"
      />
    </Box>
    <Typography
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "11.5px",
        color: QC.fgFaint,
        textAlign: "center",
        maxWidth: 520,
        marginTop: "14px",
      }}
    >
      Larger documents? Use a regular chat — quick chat skips the RAG pipeline and won't paginate huge files.
    </Typography>
  </Box>
);

const formatBytesShort = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Inline doc tag rendered inside the user bubble that carried the attachment.
// Read-only — once sent, the file is locked to that turn as context.
const MessageAttachment = ({ attachment }) => {
  if (!attachment) return null;
  return (
    <Tooltip title={formatBytesShort(attachment.size)} placement="top" arrow>
      <Box
        data-testid="quickchat-message-attachment"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "6px",
          padding: "4px 8px",
          borderRadius: "6px",
          backgroundColor: "rgba(212,168,106,0.1)",
          border: `1px solid ${QC.borderDashSoft}`,
          color: QC.fgMid,
          fontFamily: "Poppins, sans-serif",
          fontSize: "12px",
          maxWidth: "100%",
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: "14px", color: QC.accentBolt }} />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 220,
          }}
        >
          {attachment.name}
        </Box>
      </Box>
    </Tooltip>
  );
};

const ThinkingDots = () => (
  <Box
    aria-label="Thinking"
    sx={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 0" }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: QC.accentBolt,
          animation: "qcBounce 1.2s ease-in-out infinite both",
          animationDelay: `${i * 0.16}s`,
          "@keyframes qcBounce": {
            "0%, 80%, 100%": { transform: "scale(0)" },
            "40%": { transform: "scale(1)" },
          },
        }}
      />
    ))}
  </Box>
);

const SizeErrorChip = ({ message, onSwitchToRegular }) => (
  <Box
    data-testid="quickchat-size-error"
    sx={{
      borderRadius: "8px",
      backgroundColor: "#3a2018",
      color: "#ffb4a2",
      padding: "10px 14px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "13px",
      lineHeight: 1.5,
      maxWidth: "85%",
    }}
  >
    <div>{message}</div>
    <button
      type="button"
      onClick={onSwitchToRegular}
      style={{
        marginTop: 6,
        background: "transparent",
        border: "none",
        color: "#ffd9c9",
        textDecoration: "underline",
        cursor: "pointer",
        padding: 0,
        fontFamily: "inherit",
        fontSize: "13px",
      }}
    >
      Switch to regular chat
    </button>
  </Box>
);

const MessageList = ({ messages, isStreaming, streamingMessageId, onSwitchToRegular }) => {
  if (messages.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", padding: "8px 4px" }}>
      {messages.map((m) => {
        const isBot = m.sender === "bot";
        const isThisStreaming = isBot && isStreaming && m.id === streamingMessageId;
        const showThinking = isBot && (m.isLoading || (isThisStreaming && !m.message));
        const isSizeError = isBot && m.hasError && m.errorKind === "size";

        return (
          <Box
            key={m.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isBot ? "flex-start" : "flex-end",
              padding: "0 8px",
            }}
          >
            {isSizeError ? (
              <SizeErrorChip message={m.message} onSwitchToRegular={onSwitchToRegular} />
            ) : (
              <Box
                sx={{
                  backgroundColor: isBot ? "transparent" : "#1f1a16",
                  color: m.hasError ? "#ffb4a2" : QC.fg,
                  borderRadius: "8px",
                  padding: "8px 14px",
                  maxWidth: isBot ? "85%" : "80%",
                  border: m.hasError ? "1px solid rgba(255,107,107,0.3)" : "none",
                  // Override the global `.App { text-align: center }` cascade
                  // (see App.css). Without this, markdown headings/lists/etc.
                  // inherit center alignment.
                  textAlign: "left",
                }}
              >
                {showThinking ? (
                  <ThinkingDots />
                ) : isBot ? (
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <Typography
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "14px",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            color: m.hasError ? "#ffb4a2" : QC.fg,
                            marginBottom: 1,
                          }}
                          {...props}
                        />
                      ),
                    }}
                  >
                    {m.message || ""}
                  </ReactMarkdown>
                ) : (
                  <>
                    {m.attachment && (
                      <Box sx={{ display: "block" }}>
                        <MessageAttachment attachment={m.attachment} />
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: QC.fg,
                      }}
                    >
                      {m.message}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                display: "inline-block",
                fontFamily: "Poppins, sans-serif",
                fontSize: "10.5px",
                fontWeight: 700,
                color: isBot ? "#000" : QC.fgFaint,
                background: isBot ? QC.brandYellow : "rgba(212,200,184,0.06)",
                padding: "2px 8px",
                borderRadius: "4px",
                marginTop: "6px",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {isBot ? "ACE" : "You"}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ExpiryNotice = ({ message, onDismiss }) => (
  <Box
    data-testid="quickchat-expiry-notice"
    sx={{
      margin: "8px 22px",
      padding: "8px 12px",
      borderRadius: "6px",
      backgroundColor: "rgba(212,168,106,0.12)",
      border: `1px solid ${QC.borderDashSoft}`,
      color: QC.fgSub,
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    }}
  >
    <span>{message}</span>
    <IconButton
      size="small"
      aria-label="Dismiss notice"
      onClick={onDismiss}
      sx={{ color: QC.fgMute, padding: "2px" }}
    >
      <CloseRoundedIcon fontSize="inherit" />
    </IconButton>
  </Box>
);

/**
 * The chat surface. Exposes an imperative `setDraft` so the page can hand
 * back the user's last draft after an expiry-driven reset.
 */
const IncognitoChatWindow = forwardRef(function IncognitoChatWindow(
  {
    messages,
    attachedFile,
    onAttachFile,
    onRemoveFile,
    onSend,
    onStop,
    isStreaming,
    isSending,
    streamingMessageId,
    expiryNotice,
    onDismissExpiryNotice,
  },
  ref
) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  // Ref on the scrollable message-list container (not a sentinel inside it).
  // We set scrollTop directly instead of using scrollIntoView, which would
  // cascade up and scroll the whole page — pushing the composer off-screen.
  const messageListRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setDraft: (value) => setDraft(value || ""),
    getDraft: () => draft,
  }));

  const { getRootProps, getInputProps, open: openFilePicker } = useDropzone({
    accept: ACCEPTED_TYPES,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: ([file]) => {
      if (file) onAttachFile(file);
    },
  });

  useEffect(() => {
    const list = messageListRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, isStreaming]);

  // When the session expires the hook seeds a notice with the unsent draft.
  // Restore it into the composer so the user can retry with one keystroke.
  const lastSeenNoticeRef = useRef(null);
  useEffect(() => {
    if (expiryNotice && expiryNotice !== lastSeenNoticeRef.current) {
      lastSeenNoticeRef.current = expiryNotice;
      if (expiryNotice.draft && !draft) {
        setDraft(expiryNotice.draft);
      }
    }
    if (!expiryNotice) {
      lastSeenNoticeRef.current = null;
    }
  }, [expiryNotice, draft]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    onSend(text);
  };

  const handleSendOrStop = () => {
    if (isStreaming) {
      onStop?.();
    } else {
      submit();
    }
  };

  const handleSwitchToRegular = () => {
    navigate("/main-chat", { state: { quickChatDraft: draft || undefined } });
  };

  const sendDisabled = !draft.trim() && !isStreaming;

  return (
    <Box
      data-tour="quick-chat-window"
      {...getRootProps()}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 1100,
        backgroundColor: QC.bgPanel,
        border: `1px solid ${QC.border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <input {...getInputProps()} data-testid="quickchat-file-input" />

      {/* 5% hatch wallpaper overlay — pointer-events:none so it never blocks the dropzone. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: QC.hatchOverlay,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
        <StatusStrip />

        {/* Spacer where the data-source row would normally sit. Quick chat hides it. */}
        <Box sx={{ height: 8 }} />

        <Box
          ref={messageListRef}
          sx={{
            flex: 1,
            minHeight: 320,
            maxHeight: 520,
            overflowY: "auto",
            padding: "8px 18px 16px",
            scrollbarWidth: "thin",
            scrollbarColor: `${QC.borderSoft} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              background: QC.borderSoft,
              borderRadius: 3,
            },
          }}
          data-testid="quickchat-message-list"
        >
          {messages.length === 0 ? (
            <EmptyState />
          ) : (
            <MessageList
              messages={messages}
              isStreaming={isStreaming}
              streamingMessageId={streamingMessageId}
              onSwitchToRegular={handleSwitchToRegular}
            />
          )}
        </Box>

        {expiryNotice && (
          <ExpiryNotice message={expiryNotice.message} onDismiss={onDismissExpiryNotice} />
        )}

        <Box
          sx={{
            padding: "12px 18px",
            borderTop: `1px solid ${QC.borderSoft}`,
          }}
        >
          {attachedFile && (
            <Box sx={{ marginBottom: 1 }}>
              <IncognitoFileChip file={attachedFile} onRemove={onRemoveFile} />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#1f1f1f",
              borderRadius: "8px",
              padding: "8px 10px",
              border: "1px solid transparent",
              "&:focus-within": {
                borderColor: "rgba(255,195,77,0.4)",
              },
            }}
          >
            <Tooltip
              title="Attach a file (docs ≤ 25 MB · images ≤ 10 MB)"
              placement="top"
              arrow
            >
              <IconButton
                onClick={openFilePicker}
                aria-label="Attach a file"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  backgroundColor: QC.border,
                  border: `1px solid #3a2f24`,
                  color: QC.fgMid,
                  padding: 0,
                  "&:hover": { backgroundColor: "#332821" },
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 600 }}>+</span>
              </IconButton>
            </Tooltip>
            <InputBase
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendOrStop();
                }
              }}
              placeholder="Ask anything — quick chat . . ."
              inputProps={{ "aria-label": "Ask anything in quick chat" }}
              multiline
              maxRows={4}
              disabled={isStreaming}
              sx={{
                flex: 1,
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: QC.fg,
                "& input::placeholder, & textarea::placeholder": {
                  color: QC.fgFaint,
                  opacity: 1,
                },
              }}
            />
            <IconButton
              aria-label={isStreaming ? "Stop generating" : "Send message"}
              onClick={handleSendOrStop}
              disabled={!isStreaming && sendDisabled}
              sx={{
                backgroundColor: QC.brandYellow,
                borderRadius: "6px",
                color: "#000",
                width: 32,
                height: 32,
                "&:hover": { backgroundColor: "#ffb800" },
                "&.Mui-disabled": { opacity: 0.4, backgroundColor: QC.brandYellow, color: "#000" },
              }}
            >
              {isStreaming || isSending ? <StopRoundedIcon /> : <SendRoundedIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default IncognitoChatWindow;
